import React, { useEffect, useRef } from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { cloudinarySmartThumbUrl } from '../lib/cloudinary';
import {
  DetailsBackdrop,
  DetailsBody,
  DetailsClose,
  DetailsContent,
  DetailsHeader,
  DetailsMeta,
  DetailsMetaItem,
  DetailsRow,
  DetailsRowIndex,
  DetailsRowItem,
  DetailsRowText,
  DetailsSubtitle,
  DetailsTitle,
  DetailsTopActions,
  DetailsActionButton,
  DetailsHeaderMeta,
  DetailsMetaRight,
  DetailsSplit,
} from './DetailsModal.styles';

const joinOrDash = (values) => {
  if (!values || values.length === 0) return '—';
  return values.join(', ');
};

const getCardDimensions = (cardStyle) => (cardStyle === 'portrait'
  ? { width: 420, height: 630 }
  : { width: 640, height: 360 });

const getThumbGravity = (media) => {
  if (media.thumbnailGravity) return media.thumbnailGravity;
  if (media.useCustomGravity) return 'g_custom';
  return 'g_auto:faces';
};

function DetailsModal({
  isOpen,
  onClose,
  album,
  hero,
  items,
  onPlayAlbum,
  onSelectItem,
  heroImageOverride,
  cardStyle,
}) {
  const playButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    if (!playButtonRef.current) return;
    playButtonRef.current.focus();
  }, [isOpen]);

  if (!isOpen || !album) return null;

  const meta = hero?.descriptors || {};

  return (
    <DetailsBackdrop onClick={onClose}>
      <DetailsContent onClick={(event) => event.stopPropagation()}>
        <DetailsHeader
          backgroundUrl={heroImageOverride || hero?.imagePath || hero?.backgroundPath}
        >
          <DetailsTopActions>
            <DetailsActionButton ref={playButtonRef} type="button" $primary onClick={onPlayAlbum}>
              ▶ Play
            </DetailsActionButton>
            <DetailsActionButton type="button">＋</DetailsActionButton>
            <DetailsActionButton type="button">👍</DetailsActionButton>
          </DetailsTopActions>
          <DetailsClose type="button" onClick={onClose} aria-label="Close">
            &#10005;
          </DetailsClose>
          <div>
            <DetailsTitle>{hero?.title || album.title}</DetailsTitle>
            <DetailsHeaderMeta>
              <span>{hero?.matchPercent || 98}% Match</span>
              {hero?.year ? <span>{hero.year}</span> : null}
              {hero?.rating ? <span>{hero.rating}</span> : null}
              {hero?.seasonCount ? <span>{hero.seasonCount} Season</span> : null}
            </DetailsHeaderMeta>
          </div>
        </DetailsHeader>
        <DetailsBody>
          <DetailsMeta>
            <DetailsMetaItem>{hero?.description || album.description}</DetailsMetaItem>
          </DetailsMeta>
        </DetailsBody>
        <DetailsSubtitle>{album.title}</DetailsSubtitle>
        <DetailsSplit>
          <DetailsRow>
            {items.map((item, idx) => (
              <DetailsRowItem
                key={item.id}
                onClick={() => onSelectItem(item)}
                $variant={cardStyle}
              >
                <DetailsRowIndex>{idx + 1}</DetailsRowIndex>
                {item.imagePath || item.videoPosterPath ? (
                  <img
                    src={cloudinarySmartThumbUrl(
                      item.thumbnailOverridePath || item.imagePath || item.videoPosterPath,
                      {
                        ...getCardDimensions(cardStyle),
                        gravity: getThumbGravity(item),
                      },
                    )}
                    alt={item.title}
                  />
                ) : item.videoPath ? (
                  <video
                    src={item.videoPath}
                    muted
                    playsInline
                    preload="metadata"
                  />
                ) : null}
                <DetailsRowText>
                  <strong>{item.title}</strong>
                  <span>{item.description}</span>
                </DetailsRowText>
            </DetailsRowItem>
          ))}
          </DetailsRow>
          <DetailsMetaRight>
            <DetailsMetaItem>
              Cast: {joinOrDash(meta.cast)}
            </DetailsMetaItem>
            <DetailsMetaItem>
              Genre: {joinOrDash(meta.genre)}
            </DetailsMetaItem>
            <DetailsMetaItem>
              Occasion: {meta.occasion || '—'}
            </DetailsMetaItem>
          </DetailsMetaRight>
        </DetailsSplit>
      </DetailsContent>
    </DetailsBackdrop>
  );
}

export default DetailsModal;

DetailsModal.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  album: shape,
  hero: shape,
  items: arrayOf(shape),
  onPlayAlbum: func,
  onSelectItem: func,
  heroImageOverride: string,
  cardStyle: string,
}.isRequired;
